import { useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface SearchResultRow {
  readonly documentId: string;
  readonly documentCode: string;
  readonly documentName: string;
  readonly classification: string;
  readonly status: string;
}

interface SearchResponse {
  readonly success: boolean;
  readonly data: SearchResultRow[];
  readonly total: number;
}

export function M23SearchCenterPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [text, setText] = useState('');
  const [rows, setRows] = useState<SearchResultRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function onSearch(e: FormEvent): void {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setRows(null);
    const qs = new URLSearchParams({ text });
    apiRequest<SearchResponse>(`/v1/document-search?${qs.toString()}`, { token })
      .then((r) => { setRows(r.data); setTotal(r.total); })
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }

  return (
    <main aria-label="search-center">
      <h1>{t('m23.search.title')}</h1>
      <form aria-label="search-form" onSubmit={onSearch}>
        <label htmlFor="text">{t('m23.search.searchText')}</label>
        <input id="text" value={text} onChange={(e) => setText(e.target.value)} />
        <button type="submit">{t('m23.search.search')}</button>
      </form>
      {error && <p role="alert">{error}</p>}
      {loading && <p>{t('m23.search.loading')}</p>}
      {rows && (
        <>
          <p aria-label="result-count">{t('m23.search.resultCount', { count: total })}</p>
          <table aria-label="search-results-table">
            <thead><tr><th>{t('m23.search.colCode')}</th><th>{t('m23.search.colName')}</th><th>{t('m23.search.colClassification')}</th><th>{t('m23.search.colStatus')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.documentId}>
                  <td>{r.documentCode}</td>
                  <td>{r.documentName}</td>
                  <td>{r.classification}</td>
                  <td>{r.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
